import React, { ReactElement, useEffect } from "react"
import {
  hasQuestionBeenSkipped,
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useIntl } from "react-intl"
import {
  ButtonFooterLayout,
  ConfirmButton,
  ContinueButton,
  RoutingFlowTitle,
  SkipButton,
  StepLayout
} from "#/components/carrot-plans/shared"
import { Form, FormTextField, useWatch, useFormContext } from "@carrotfertility/carotene-core-x"
import { sanitizeTextInput } from "#/utils/sanitizeTextInput"
import { FormRadioButtonGroup } from "#/services/common-forms"

interface IsThereAnythingElseStepProps {
  header: string
  description: string
  allowSkip?: boolean
  requireInput?: boolean
  continueButtonMessage: string
  isSensitiveScenario?: boolean
}

function IsThereAnythingElseStep({
  header,
  description,
  allowSkip,
  requireInput,
  continueButtonMessage
}: IsThereAnythingElseStepProps): ReactElement {
  const intl = useIntl()
  const { mutateAsync } = useUpdateRoutingInfo()
  const routingData = useCheckRoutingInfoQueryCache()
  const defaultValue = routingData?.memberJourneyAdditionalInfo
  const { send } = useStateMachine(
    defaultValue || hasQuestionBeenSkipped("memberJourneyAdditionalInfo", routingData) ? "" : null
  )

  async function onSkip(): Promise<void> {
    await mutateAsync(
      { memberJourneyAdditionalInfo: null, skipFields: ["memberJourneyAdditionalInfo"] },
      { onSuccess: () => send("") }
    )
  }

  async function onSubmit(formValues: { memberJourneyAdditionalInfo?: string }): Promise<void> {
    const sanitizedInput = sanitizeTextInput(formValues?.memberJourneyAdditionalInfo)
    if (sanitizedInput === null && allowSkip) {
      await onSkip()
    } else {
      await mutateAsync({ memberJourneyAdditionalInfo: sanitizedInput }, { onSuccess: () => send("") })
    }
  }

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id="memberJourneyAdditionalInfo-label">{header}</span>}
        description={description}
        formElements={
          <Form onSubmit={onSubmit}>
            <FormTextField
              name="memberJourneyAdditionalInfo"
              defaultValue={defaultValue}
              rows={4}
              multiline
              registerOptions={
                requireInput
                  ? {
                      validate: (inputValue) =>
                        sanitizeTextInput(inputValue) === null
                          ? intl.formatMessage({ defaultMessage: "Please add more detail." })
                          : true,
                      required: intl.formatMessage({ defaultMessage: "Please add more detail." })
                    }
                  : undefined
              }
              inputProps={{ maxLength: 3000, "aria-labelledby": "memberJourneyAdditionalInfo-label" }}
            />
            <ButtonFooterLayout
              skipButton={allowSkip ? <SkipButton onClick={onSkip} /> : undefined}
              continueButton={<ConfirmButton>{continueButtonMessage}</ConfirmButton>}
            />
          </Form>
        }
      />
    </>
  )
}

export function IsThereAnythingElseCheckInStep(): ReactElement {
  const intl = useIntl()
  const header = intl.formatMessage({
    defaultMessage: "Is there anything else you’d like to share as part of this update?"
  })
  const description = intl.formatMessage({
    defaultMessage:
      "This could include information about your journey to this point, any specific issues you would like support for, or any other considerations you’d like us to be aware of so we can personalize your care."
  })
  const continueButtonMessage = intl.formatMessage({
    defaultMessage: "Update Carrot Plan"
  })

  return (
    <IsThereAnythingElseStep
      header={header}
      description={description}
      allowSkip={true}
      requireInput={false}
      continueButtonMessage={continueButtonMessage}
    />
  )
}

export function IsThereAnythingElseAnythingElseSensitiveScenarioStep(): ReactElement {
  const routingInfo = useCheckRoutingInfoQueryCache()
  const { mutateAsync } = useUpdateRoutingInfo()
  const defaultButtonValue = routingInfo?.sensitiveScenarioSupport
  const defaultTextValue = routingInfo?.memberJourneyAdditionalInfo
  const { send } = useStateMachine(defaultButtonValue ? "" : null)
  const intl = useIntl()

  const header = intl.formatMessage({
    defaultMessage: "We’re so sorry to hear that. How can we best support you right now?"
  })
  const description = intl.formatMessage({
    defaultMessage:
      "We can update your Carrot Plan with new suggestions, such as emotional support chats, educational resources, or help finding a provider. If you're not interested in additional support at this time, just come back whenever you're ready to share an update."
  })
  const continueButtonMessage = intl.formatMessage({
    defaultMessage: "Continue"
  })

  const sensitiveScenarioSupportOptions = [
    { label: intl.formatMessage({ defaultMessage: "Update my Carrot Plan" }), value: "UPDATE_CARROT_PLAN" },
    {
      label: intl.formatMessage({ defaultMessage: "I don't want new suggestions right now" }),
      value: "NO_CARROT_PLAN_NEEDED"
    }
  ]

  const onSubmit = async (formValues: { sensitiveScenarioSupport?: string; memberJourneyAdditionalInfo: string }) => {
    const sanitizedInput = sanitizeTextInput(formValues?.memberJourneyAdditionalInfo)
    await mutateAsync(
      {
        memberJourneyAdditionalInfo: sanitizedInput,
        sensitiveScenarioSupport: formValues?.sensitiveScenarioSupport
      },
      { onSuccess: () => send("") }
    )
  }

  const SensitiveScenarioFormTextField = () => {
    const sensitiveSupportValue = useWatch({ name: "sensitiveScenarioSupport" })
    const methods = useFormContext()

    useEffect(() => {
      if (sensitiveSupportValue === "NO_CARROT_PLAN_NEEDED") {
        methods.clearErrors()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
    }, [sensitiveSupportValue])

    return (
      <FormTextField
        name="memberJourneyAdditionalInfo"
        defaultValue={defaultTextValue}
        rows={4}
        label={intl.formatMessage({ defaultMessage: "More details" })}
        helperText={intl.formatMessage({
          defaultMessage: "Share as much as you’d like about your journey or how we can help.  "
        })}
        multiline
        registerOptions={{
          required:
            sensitiveSupportValue === "UPDATE_CARROT_PLAN"
              ? intl.formatMessage({ defaultMessage: "Please add more detail." })
              : false
        }}
        inputProps={{ maxLength: 3000, "aria-labelledby": "memberJourneyAdditionalInfo-label" }}
      />
    )
  }

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id="sensitiveScenarioSupport-label">{header}</span>}
        description={description}
        formElements={
          <Form onSubmit={onSubmit}>
            <FormRadioButtonGroup
              name="sensitiveScenarioSupport"
              options={sensitiveScenarioSupportOptions}
              controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
              defaultValue={defaultButtonValue}
              aria-labelledby={"sensitiveScenarioSupport-label"}
            />
            <SensitiveScenarioFormTextField />
            <ButtonFooterLayout continueButton={<ContinueButton>{continueButtonMessage}</ContinueButton>} />
          </Form>
        }
      />
    </>
  )
}
